import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { advertiserApi } from '../../services/advertiserApi';
import { useAuthStore } from '../../stores/authStore';
import { ApiError } from '../../services/api';

/**
 * Same password rules as the regular RegisterPage (mirrors the API
 * schema). We add `confirmPassword` on the client only — the API never
 * sees it.
 */
const RegisterAdvertiserSchema = z
  .object({
    companyName: z
      .string()
      .min(1, 'Company name is required.')
      .max(255, 'Company name is too long.'),
    contactEmail: z
      .string()
      .min(1, 'Contact email is required')
      .email('Please enter a valid contact email.'),
    email: z
      .string()
      .min(1, 'Account email is required')
      .email('Please enter a valid account email.'),
    password: z
      .string()
      .min(10, 'Password must be at least 10 characters long.')
      .max(200, 'Password is too long.'),
    confirmPassword: z.string().min(1, 'Please re-enter your password.'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords don’t match.',
  });

type Fields = z.infer<typeof RegisterAdvertiserSchema>;

function registerErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.httpStatus === 409) {
      return 'An account with that email already exists. Try signing in instead.';
    }
    if (err.httpStatus === 400 && err.serverMessage) {
      return err.serverMessage;
    }
    if (err.httpStatus === 429) {
      return 'Too many attempts. Please wait a minute and try again.';
    }
  }
  return 'We couldn’t create your advertiser account. Please try again.';
}

/**
 * Advertiser registration page (spec §9.4 / §30). Collects the
 * advertiser-specific fields on top of the regular auth pair, calls the
 * new `/api/auth/register-advertiser` endpoint, and routes the new
 * advertiser to `/dashboard/advertiser` on success.
 */
export function RegisterAdvertiserPage() {
  const navigate = useNavigate();
  const signIn = useAuthStore((s) => s.signIn);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Fields>({
    resolver: zodResolver(RegisterAdvertiserSchema),
    defaultValues: {
      companyName: '',
      contactEmail: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  async function onSubmit(values: Fields) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const bundle = await advertiserApi.register({
        email: values.email,
        password: values.password,
        companyName: values.companyName,
        contactEmail: values.contactEmail,
      });
      signIn(bundle);
      navigate('/dashboard/advertiser', { replace: true });
    } catch (err) {
      setSubmitError(registerErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
          Advertise on Round Recon
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create campaigns, sponsor placements, and access aggregated market intelligence. Your
          account starts in review status — an admin will activate it before any sponsored placement
          goes live.
        </Typography>

        {submitError ? (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          aria-label="Create advertiser account"
        >
          <Stack spacing={2}>
            <TextField
              label="Company name"
              required
              fullWidth
              autoComplete="organization"
              {...register('companyName')}
              error={Boolean(errors.companyName)}
              helperText={errors.companyName?.message ?? ' '}
              inputProps={{ 'aria-invalid': Boolean(errors.companyName) }}
            />
            <TextField
              label="Contact email"
              type="email"
              required
              fullWidth
              autoComplete="email"
              {...register('contactEmail')}
              error={Boolean(errors.contactEmail)}
              helperText={
                errors.contactEmail?.message ??
                'Used for billing notifications + admin communication.'
              }
              inputProps={{ 'aria-invalid': Boolean(errors.contactEmail) }}
            />
            <TextField
              label="Account email"
              type="email"
              required
              fullWidth
              autoComplete="username"
              {...register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message ?? 'Used to sign in to the advertiser dashboard.'}
              inputProps={{ 'aria-invalid': Boolean(errors.email) }}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              {...register('password')}
              error={Boolean(errors.password)}
              helperText={errors.password?.message ?? 'At least 10 characters.'}
              inputProps={{ 'aria-invalid': Boolean(errors.password) }}
            />
            <TextField
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              {...register('confirmPassword')}
              error={Boolean(errors.confirmPassword)}
              helperText={errors.confirmPassword?.message ?? ' '}
              inputProps={{ 'aria-invalid': Boolean(errors.confirmPassword) }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Creating account…' : 'Create advertiser account'}
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" sx={{ mt: 3 }}>
          Already have an account?{' '}
          <MuiLink component={RouterLink} to="/login">
            Sign in
          </MuiLink>
          .
        </Typography>
      </Paper>
    </Box>
  );
}

export default RegisterAdvertiserPage;
